import { SiteUrls } from '@tableau/shared-urls';
import {
  TextLinkKind,
  TextLinkWidget
} from '@tableau/widgets';
import * as React from 'react';
import * as _ from 'underscore';

import { Databases } from '../../catalog/resources/Databases';
import { Messages } from '../../codegen/Localize';
import { imgPath } from '../../common/utilities/ImgUrl';
import {
  IEmptyResult,
  IResult
} from '../../httpClient/serverApi/types/IResponse';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import { AuthorizableType } from '../../permissions/types/AuthorizableType';
import {
  LockStatusHelper,
  LockUnlockPermissionsState,
  NestedProjectsPermissionsState
} from '../../permissions/utils/LockStatusHelper';
import { Projects } from '../../projects/resources/Projects';
import {
  ErrorReason,
  getSessionManagementServiceInstance
} from '../../sessionManagement/SessionManagementService';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { CommonErrorCode } from '../../ts/helpers/ErrorCodes';
import { L10n } from '../../ts/L10n/L10n';
import { IProjectDetailInfo } from '../../ts/models/IProjectInfo';
import { Set } from '../../ts/models/Set';
import { ISetContentControlledPermissionsParams } from '../../ts/ServerApi/requests/SetContentControlledPermissionsRequest';
import { ISetProjectControlledPermissionsParams } from '../../ts/ServerApi/requests/SetProjectControlledPermissionsRequest';
import { ITypedIdWithName } from '../../ts/ServerApi/types/ITypedId';
import {
  IToastOptions,
  ToasterService,
  ToastType
} from '../../ts/services/ToasterService';
import { LongRunningAction } from '../services/LongRunningAction';
import { ILongRunningAction } from '../services/types/ILongRunningAction';
import { PermissionsLockUnlockStyles } from '../styles/PermissionsLockUnlockStyle';
import { isAuthorizableTypeContainer } from '../types/AuthorizableType';
import { PermissionsLockDialog } from './PermissionsLockDialog';

export const EDIT_LINK_TESTID = 'lock-unlock-project-permissions';
export const CONTROLLING_CONTENT_LINK_TESTID = 'controlling-content';
export const UNLOCK_IN_PROGRESS_TESTID = 'unlock-in-progress';
export const PERMISSIONS_LOCK_LABEL_TESTID = 'permissions-lock-label';
export const PERMISSIONS_PROGRESS_BAR_TESTID = 'permissions-progress-bar';
export const PERMISSIONS_PROGRESS_NUMBER_TESTID = 'permissions-progress-number';

export interface ControllingContent {
  name: string;
  url: string;
}

export interface ContainerMessages {
  titleSingle: () => string;
  titleMultiple: (a: {CONTAINER_COUNT: number, CONTAINER_COUNT_LOC: string}) => string;
  changeSuccessSingle: (a: {CONTAINER_NAME: string, PERMISSION_SOURCE: string}) => string;
  changeSuccessMultiple: (a: {CONTAINER_COUNT: number, CONTAINER_COUNT_LOC: string, PERMISSION_SOURCE: string}) => string;
  changeSuccessLocked: (a: {CONTAINER_COUNT: number}) => string;
  notFound: (a: {CONTAINER_NAME: string}) => string;
  warningSingle: (a: {CONTAINER_NAME: string}) => string;
  warningMultiple: (a: {CONTAINER_COUNT: number, CONTAINER_COUNT_LOC: string}) => string;
  warningIncludingNestedSingle?: (a: {CONTAINER_NAME: string}) => string;
  warningIncludingNestedMultiple?: (a: {CONTAINER_COUNT: number, CONTAINER_COUNT_LOC: string}) => string;
  lockedDescription: () => string;
  lockedIncludingNestedDescription?: () => string;
  lockedNestedDescription?: () => string;
  lockedRadioButtonLabel: () => string;
  mixedDescription: () => string;
  unlockedDescription: () => string;
  unlockedRadioButtonLabel: () => string;
}

export const DatabaseMessages: ContainerMessages = {
  titleSingle: Messages.lockContainerPermissions_dialogTitleDatabase_single,
  titleMultiple: Messages.lockContainerPermissions_dialogTitleDatabase_multiple,
  changeSuccessSingle: Messages.lockContainerPermissions_lockPermissionsChangeSuccessDatabase_single,
  changeSuccessMultiple: Messages.lockContainerPermissions_lockPermissionsChangeSuccessDatabase_multiple,
  changeSuccessLocked: Messages.lockContainerPermissions_lockPermissionsChangeSuccessLockedDatabase,
  notFound: Messages.lockContainerPermissions_errorContainerNotFoundDatabase,
  warningSingle: Messages.lockContainerPermissions_lockWarningDatabase_single,
  warningMultiple: Messages.lockContainerPermissions_lockWarningDatabase_multiple,
  lockedDescription: Messages.lockPermissions_controllingDatabaseLocked,
  lockedRadioButtonLabel: Messages.lockContainerPermissions_lockDescriptionDatabase,
  mixedDescription: Messages.lockPermissions_controllingDatabaseMixed,
  unlockedDescription: Messages.lockPermissions_controllingDatabaseUnlocked,
  unlockedRadioButtonLabel: Messages.lockContainerPermissions_unlockDescriptionDatabase,
};

export const ProjectMessages: ContainerMessages = {
  titleSingle: Messages.lockContainerPermissions_dialogTitleProject_single,
  titleMultiple: Messages.lockContainerPermissions_dialogTitleProject_multiple,
  changeSuccessSingle: Messages.lockContainerPermissions_lockPermissionsChangeSuccessProject_single,
  changeSuccessMultiple: Messages.lockContainerPermissions_lockPermissionsChangeSuccessProject_multiple,
  changeSuccessLocked: Messages.lockContainerPermissions_lockPermissionsChangeSuccessLockedProject,
  notFound: Messages.lockContainerPermissions_errorContainerNotFoundProject,
  warningSingle: Messages.lockContainerPermissions_lockWarningProject_single,
  warningMultiple: Messages.lockContainerPermissions_lockWarningProject_multiple,
  warningIncludingNestedSingle: Messages.lockContainerPermissions_lockWarningProjectAndNestedProjects_single,
  warningIncludingNestedMultiple: Messages.lockContainerPermissions_lockWarningProjectAndNestedProjects_multiple,
  lockedDescription: Messages.lockPermissions_controllingProjectLocked,
  lockedIncludingNestedDescription: Messages.lockPermissions_controllingProjectLockedIncludingSubprojects,
  lockedNestedDescription: Messages.lockPermissions_nestedLockedProject,
  lockedRadioButtonLabel: Messages.lockContainerPermissions_lockDescriptionProject,
  mixedDescription: Messages.lockPermissions_controllingProjectMixed,
  unlockedDescription: Messages.lockPermissions_controllingProjectUnlocked,
  unlockedRadioButtonLabel: Messages.lockContainerPermissions_unlockDescriptionProject,
};

export type PermissionsLockUnlockProps = {
  /** The authorizables that this component is for */
  authorizables: Set<any>;

  /** Type of authorizable this is for */
  authorizableType: AuthorizableType;

  /** The icon (if any) to render if the authorizable is not a container */
  icon: string;

  /** The string to render if the authorizable type is not a container */
  nonContainerMessage: string;

  /** ID of an authorizable this component is for to the content that controls its permissions */
  sourceAuthorizablesById: _.Dictionary<ITypedIdWithName>;

  /** Callback for when a change to lock status has successfully finished */
  updateLockStatus: (lockState: LockUnlockPermissionsState, nestedProjectsState: NestedProjectsPermissionsState) => void;
};

export type PermissionsLockUnlockState = {
  showDialog: boolean;
  busyUnlocking: boolean;
  percentComplete: number;
  controllingContent?: ControllingContent; // the content that controls all the authorizables this component is for
};

export class PermissionsLockUnlock extends React.Component<PermissionsLockUnlockProps, PermissionsLockUnlockState> {
  private containerMessages: ContainerMessages;
  private siteUrls: SiteUrls;

  constructor(props: PermissionsLockUnlockProps) {
    super(props);
    this.setContainerMessages();
    this.setSiteUrls();
    this.state = {
      showDialog: false,
      busyUnlocking: false,
      percentComplete: 0,
    };
  }

  public componentDidMount() {
    this.setControllingContent();
  }

  public componentDidUpdate(prevProps: PermissionsLockUnlockProps) {
    // setControllingContent needs to recalculate the controlling content when sourceAuthorizablesById has changed
    if (!_.isEqual(prevProps.sourceAuthorizablesById, this.props.sourceAuthorizablesById)) {
      this.setControllingContent();
    }
  }

  private setContainerMessages = (): void => {
    switch(this.props.authorizableType) {
      case AuthorizableType.PROJECT:
        this.containerMessages = ProjectMessages;
        break;
      case AuthorizableType.DATABASE:
        this.containerMessages = DatabaseMessages;
        break;
    }
  }

  private setSiteUrls = (): void => {
    this.siteUrls = new SiteUrls({
      siteUrlName: getSessionManagementServiceInstance().sessionSiteUrlName(),
      queryString: window.location.search,
    });
  }

  private setControllingContent = (): void => {
    // For now this method only handles the case where the controlling content is Project.
    // However, the controlling content could also be Database/Table (if authorizableType is Table) or Workbook/View (if authorizableType is View)
    // TODO: TFS 1029168 for handling other possible types of controlling content
    if (FeatureFlags.lockingSubProjectsEnabled && this.props.sourceAuthorizablesById[this.props.authorizables.first().id]) {
      const controllingContent: ITypedIdWithName = this.props.sourceAuthorizablesById[this.props.authorizables.first().id];
      if (controllingContent.id && controllingContent.type && controllingContent.type === AuthorizableType.PROJECT) {
        // check if all authorizables are controlled by the same project
        const allControlledBySameProject: boolean = this.props.authorizables.all(authorizable => {
          const controllingContentIdToCompare: string = this.props.sourceAuthorizablesById[authorizable.id] && this.props.sourceAuthorizablesById[authorizable.id].id;
          return controllingContentIdToCompare === controllingContent.id;
        });
        if (allControlledBySameProject) {
          // check if current user can see the controlling project, if not then getById will not return IProjectDetailInfo
          Projects.getById(controllingContent.id).then((value: IProjectDetailInfo) => {
            this.setState({
              controllingContent: {
                name: value.name,
                url: this.siteUrls.projectUrl(value.id),
              },
            });
          }, (reason: any) => {
            // A rejection here likely means the user does not have permission to access the controlling project, so no need to update controllingContent
          });
        }
      }
    }
  }

  private popToast = (type: ToastType, message: string): void => {
    let toast: IToastOptions;
    switch (type) {
      case ToastType.Error:
        toast = {
          type,
          text: message,
        };
        break;
      case ToastType.Info:
        const count = this.props.authorizables.size();
        const text = count === 1 ?
          this.containerMessages.changeSuccessSingle({
            CONTAINER_NAME: L10n.elideIfLong(this.props.authorizables.first().name),
            PERMISSION_SOURCE: message,
          }) :
          this.containerMessages.changeSuccessMultiple({
            CONTAINER_COUNT: count,
            CONTAINER_COUNT_LOC: L10n.localizedCount(count),
            PERMISSION_SOURCE: message,
          });
        toast = { type, text, };
        break;
    }
    ToasterService.instance.pop(toast);
  }

  private handleLockResult = (result: IEmptyResult, containerNotFoundError: CommonErrorCode): IEmptyResult => {
    if (result.errors && result.errors.length) {
      const errorMessage: string = result.errors[0].code === containerNotFoundError ?
        this.containerMessages.notFound({ CONTAINER_NAME: L10n.elideIfLong(this.props.authorizables.first().name), }) :
        Messages.errorUnknown();
      this.popToast(ToastType.Error, errorMessage);
    } else {
      this.popToast(ToastType.Info, this.containerMessages.changeSuccessLocked({ CONTAINER_COUNT: this.props.authorizables.size(), }));
    }
    return result;
  }

  private lockProjectPermissions = (nestedProjectsPermissionsIncluded?: boolean): Promise<IEmptyResult> => {
    let setProjectControlledPermissionsParams: ISetProjectControlledPermissionsParams = {
      ids: this.props.authorizables.pluck('id'),
    };

    if (FeatureFlags.lockingSubProjectsEnabled) {
      setProjectControlledPermissionsParams.nestedProjectsPermissionsIncluded = nestedProjectsPermissionsIncluded;
    }

    return Projects.setProjectControlledPermissions(setProjectControlledPermissionsParams)
    .then((result: IEmptyResult) => { return this.handleLockResult(result, CommonErrorCode.ProjectNotFound); });
  }

  private lockDatabasePermissions = (): Promise<IEmptyResult> => {
    const ids: string[] = this.props.authorizables.pluck('id');

    return Databases.enableDatabaseControlledPermissions(ids)
    .then((result: IEmptyResult) => { return this.handleLockResult(result, CommonErrorCode.DatabaseNotFound); });
  }

  private unlockProjectPermissions = (nestedProjectsPermissionsIncluded?: boolean): Promise<any> => {
    const permissionsChangeSuccessCallback = (result: ILongRunningAction) => {
      if (result && !result.succeeded) {
        permissionsChangeErrorCallback(ErrorReason.Unknown);
      } else {
        this.popToast(ToastType.Info, Messages.lockProjectPermissionsChangeSuccess_unlocked({ PROJECT_COUNT: this.props.authorizables.size() }));
        this.setState({ busyUnlocking: false, percentComplete: result.percentComplete, showDialog: false, });
      }
      return result.response;
    };

    const permissionsChangeErrorCallback = (reason: ErrorReason) => {
      this.popToast(ToastType.Error, Messages.errorUnknown());
      this.setState({ busyUnlocking: false, showDialog: false, });
    };

    const progressNotifyCallback = (action: ILongRunningAction) => {
      this.setState({ busyUnlocking: true, percentComplete: action.percentComplete, });
    };

    let setContentControlledPermissionsParams: ISetContentControlledPermissionsParams = {
      ids: this.props.authorizables.pluck('id'),
    };

    if (FeatureFlags.lockingSubProjectsEnabled) {
      setContentControlledPermissionsParams.nestedProjectsPermissionsIncluded = nestedProjectsPermissionsIncluded;
    }
    return Projects.setContentControlledPermissions(setContentControlledPermissionsParams)
      .then(LongRunningAction.getActionIdFromActionResult)
      .then((actionId: string) => LongRunningAction.trackAsyncJobProgressWithPromise(actionId, progressNotifyCallback, /* delayResultForSolr */ false))
      .then(permissionsChangeSuccessCallback, permissionsChangeErrorCallback);
  }

  private unlockDatabasePermissions = (): Promise<any> => {
    const permissionsChangeSuccessCallback = (result: ILongRunningAction) => {
      if (result && !result.succeeded) {
        permissionsChangeErrorCallback(ErrorReason.Unknown);
      } else {
        this.popToast(ToastType.Info, Messages.lockDatabasePermissionsChangeSuccess_unlocked({ DATABASE_COUNT: this.props.authorizables.size() }));
        this.setState({ busyUnlocking: false, percentComplete: result.percentComplete, showDialog: false, });
      }
      return result.response;
    };

    const permissionsChangeErrorCallback = (reason: ErrorReason) => {
      this.popToast(ToastType.Error, Messages.errorUnknown());
      this.setState({ busyUnlocking: false, showDialog: false, });
    };

    const progressNotifyCallback = (action: ILongRunningAction) => {
      this.setState({ busyUnlocking: true, percentComplete: action.percentComplete, });
    };

    const ids: string[] = this.props.authorizables.pluck('id');

    return Databases.disableDatabaseControlledPermissions(ids)
      .then(LongRunningAction.getActionIdFromActionResult)
      .then((actionId: string) => LongRunningAction.trackAsyncJobProgressWithPromise(actionId, progressNotifyCallback, /* delayResultForSolr */ false))
      .then(permissionsChangeSuccessCallback, permissionsChangeErrorCallback);
  }

  private requestUpdateLockStatus = (newLockState: LockUnlockPermissionsState, newNestedProjectsState?: NestedProjectsPermissionsState) => {
    switch(this.props.authorizableType) {
      case AuthorizableType.PROJECT:
        const nestedProjectsPermissionsIncluded = newNestedProjectsState === NestedProjectsPermissionsState.Included;
        if (newLockState === LockUnlockPermissionsState.Locked) {
          return this.lockProjectPermissions(nestedProjectsPermissionsIncluded);
        } else {
          return this.unlockProjectPermissions(nestedProjectsPermissionsIncluded);
        }
      case AuthorizableType.DATABASE:
      if (newLockState === LockUnlockPermissionsState.Locked) {
        return this.lockDatabasePermissions();
      } else {
        return this.unlockDatabasePermissions();
      }
    }
  }

  private handleButtonClick = (): void => {
    this.setState({ showDialog: true, });
  }

  private handleCloseDialog = (): void => {
    this.setState({ showDialog: false, });
  }

  private handleUpdateLockStatus = (newLockState: LockUnlockPermissionsState, newNestedProjectsState?: NestedProjectsPermissionsState): void => {
    this.requestUpdateLockStatus(newLockState, newNestedProjectsState).then((result: IResult) => {
      if (!ResultHelper.hasErrors(result)) {
        this.props.updateLockStatus(newLockState, newNestedProjectsState);
        this.handleCloseDialog();
      }
    });
  }

  /** returns whether the the authorizables allow for locking permissions (they are containers they control their own permissions) */
  private canLockPermissions = (): boolean => {
    if (FeatureFlags.lockingSubProjectsEnabled) {
      // is a container and controls its own permissions
      return isAuthorizableTypeContainer(this.props.authorizableType) && !this.props.authorizables.any(p => this.props.sourceAuthorizablesById[p.id] && p.id !== this.props.sourceAuthorizablesById[p.id].id);
    }
    // is a project and there are no children projects or is a database
    return this.props.authorizableType === AuthorizableType.DATABASE || (this.props.authorizableType === AuthorizableType.PROJECT && !this.props.authorizables.any(p => !p.topLevelProject));
  }

  private getLockMessage = (lockState: LockUnlockPermissionsState, nestedProjectsState: NestedProjectsPermissionsState): string => {
    if (isAuthorizableTypeContainer(this.props.authorizableType)) {
      if (FeatureFlags.lockingSubProjectsEnabled) {
        switch (lockState) {
          case LockUnlockPermissionsState.Locked:
            if (this.props.authorizableType === AuthorizableType.PROJECT) {
              switch (nestedProjectsState) {
                case NestedProjectsPermissionsState.Included:
                  // if it cannot lock permissions, it is a nested project in a locked tree and is controlled by a parent
                  return this.canLockPermissions() ?
                    this.containerMessages.lockedIncludingNestedDescription() :
                    this.containerMessages.lockedNestedDescription();
                case NestedProjectsPermissionsState.Excluded:
                  return this.containerMessages.lockedDescription();
                case NestedProjectsPermissionsState.Mixed:
                  return this.containerMessages.mixedDescription();
              }
            }
            return this.containerMessages.lockedDescription();
          case LockUnlockPermissionsState.Unlocked:
            return this.containerMessages.unlockedDescription();
          case LockUnlockPermissionsState.Mixed:
            return this.containerMessages.mixedDescription();
        }
      }
      return LockStatusHelper.getLegacyLockMessage(lockState);
    } else {
      return this.props.nonContainerMessage;
    }
  }

  private getLockIcon = (lockState: LockUnlockPermissionsState): string => {
    if (isAuthorizableTypeContainer(this.props.authorizableType)) {
      switch (lockState) {
        case LockUnlockPermissionsState.Locked:
          return PermissionsLockUnlockStyles.LockedIcon;
        case LockUnlockPermissionsState.Unlocked:
          return PermissionsLockUnlockStyles.UnlockedIcon;
        case LockUnlockPermissionsState.Mixed:
          return null;
      }
    } else {
      return this.props.icon;
    }
  }

  private renderContent = (lockState: LockUnlockPermissionsState, nestedProjectsState?: NestedProjectsPermissionsState): JSX.Element => {
    return this.state.busyUnlocking ? this.renderBusyUnlocking() : this.renderNotBusy(lockState, nestedProjectsState);
  }

  private renderNotBusy = (lockState: LockUnlockPermissionsState, nestedProjectsState: NestedProjectsPermissionsState): JSX.Element => {
    // when locking subprojects feature flag is on, sourceAuthorizablesById is necessary to figure out the right lock message and link to show,
    // so in that case render nothing until sourceAuthorizablesById is available
    const lockIcon = this.getLockIcon(lockState);
    /* eslint-disable jsx-a11y/alt-text */
    return FeatureFlags.lockingSubProjectsEnabled && _.isEmpty(this.props.sourceAuthorizablesById) ? null : (
      <span data-tb-test-id="permissions-lock">
        <span data-tb-test-id={PERMISSIONS_LOCK_LABEL_TESTID} className={ PermissionsLockUnlockStyles.Label }>
          { lockIcon && <img aria-hidden={true} className={PermissionsLockUnlockStyles.StatusIcon} src={imgPath(lockIcon)}/>}
          { this.getLockMessage(lockState, nestedProjectsState) }
        </span>
        {
          this.canLockPermissions() ? this.renderEditLink() : this.state.controllingContent && this.renderControllingContentLink()
        }
      </span>
    );
  }

  private renderEditLink = (): JSX.Element => {
    return (
      <TextLinkWidget
        className={ PermissionsLockUnlockStyles.TextLink }
        kind={ TextLinkKind.Standard }
        onClick={ this.handleButtonClick }
        testId={ EDIT_LINK_TESTID }
      >
        { Messages.lockProjectPermissions_edit() }
      </TextLinkWidget>
    );
  }

  private renderControllingContentLink = (): JSX.Element => {
    return (
      <TextLinkWidget
        className={ PermissionsLockUnlockStyles.TextLink }
        href={ this.state.controllingContent.url }
        kind={ TextLinkKind.Standard }
        target="_blank"
        testId={ CONTROLLING_CONTENT_LINK_TESTID }
      >
        { this.state.controllingContent.name }
      </TextLinkWidget>
    );
  }

  private renderBusyUnlocking = (): JSX.Element => {
    return (
      <span data-tb-test-id={ UNLOCK_IN_PROGRESS_TESTID }>
        { Messages.permissionsUnlockInProgress() }
        <div
          aria-valuenow={ this.state.percentComplete }
          aria-valuemin={0}
          aria-valuemax={100}
          className={ PermissionsLockUnlockStyles.ProgressBar }
          role='progressbar'
        >
          <span className={ PermissionsLockUnlockStyles.ProgressBarBackgroundFill }>
            <div
              className={ PermissionsLockUnlockStyles.ProgressBarForegroundFill }
              data-tb-test-id={ PERMISSIONS_PROGRESS_BAR_TESTID }
              style={{ width: `${this.state.percentComplete}%` }}
            />
          </span>
          <span
            className={ PermissionsLockUnlockStyles.ProgressNumber }
            data-tb-test-id={ PERMISSIONS_PROGRESS_NUMBER_TESTID }
          >
            { this.state.percentComplete }
          </span>
        </div>
      </span>
    );
  }

  public render() {
    const lockState = LockStatusHelper.getLockState(this.props.authorizables);
    const nestedProjectsState = LockStatusHelper.getNestedProjectsState(this.props.authorizables);
    return (
      <div>
        <div className={ PermissionsLockUnlockStyles.ContentContainer }>
          { this.renderContent(lockState, nestedProjectsState) }
        </div>
        { this.state.showDialog &&
          <PermissionsLockDialog
            containerAuthorizableType={ this.props.authorizableType }
            containerMessages={ this.containerMessages }
            containersNumber={ this.props.authorizables.size() }
            containerName={ this.props.authorizables.first().name }
            onCancel={ this.handleCloseDialog }
            onSave={ this.handleUpdateLockStatus }
            initialLockState={ lockState }
            initialNestedProjectsState={ nestedProjectsState }
          />
        }
      </div>
    );
  }
}
